import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useEffectOnce } from 'react-use';

import { Location } from 'history';
import _ from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import {
  ELumaBotFlowType,
  ELumaBotQuestionType,
  ELumaBotTemplateItemRedirectType,
  ILumaBotFlowQuestion,
  ILumaBotFlowTemplate,
  useLumaBotFlowCreateAnswer,
  useLumaBotFlowCreateOneWithCurrentQuestion,
  useLumaBotFlowDeleteAnswer,
  usePatientLogin,
  usePatientVerify,
} from 'ui-model';

import { changeLocale, initApp, showMessage } from '@lib/actions';
import { normalizeLocale } from '@lib/actions/changeLocale';
import { useCurrentLanguage } from '@lib/hooks';
import configService from '@lib/services/configService';
import { addQuery } from '@lib/services/queryService';
import storageService from '@lib/services/storageService';

import { generateMessageTemplateKey, useSocketEventsDebounced } from 'hooks';
import { STORAGE_KEYS } from 'utils/constants';

import { TMessageWrapper } from './components/MessageWrapper';
import { chatContextDefaultValue, LumaBotContextProps } from './LumaBot.context';
import { getLumaBotPatientData, sanitizeLumaBotMessages, TLumaBotAnswers } from './LumaBot.utils';
import useLumaBotSettings from './useLumaBotSettings';
import { EMessageType, IMessage } from './WIP/ui-model/types';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const STATUS_CODE_FORBIDDEN = 403;

/**
 * Handle messages and updates for LumaBot
 */
export default function useLumaBotController(
  flowTemplates?: ILumaBotFlowTemplate[],
  flowId?: string,
  variant?: LumaBotContextProps['variant'],
  isPopper?: boolean,
): LumaBotContextProps {
  const [state, setState] = useState<Omit<LumaBotContextProps, 'messages'> & { messages: Partial<TMessageWrapper>[] }>(
    chatContextDefaultValue,
  );
  const dispatch = useDispatch();
  const language = useCurrentLanguage();
  const settings = useLumaBotSettings();
  const inputRef = useRef<HTMLElement | null>(null);
  const currentFlowIdRef = useRef('');
  const currentFlowTemplateIdRef = useRef('');
  const currentAvailabilitiesListRef = useRef<Record<string, unknown>>({});

  // The last item being displayed
  const currentItemRef = useRef<ILumaBotFlowQuestion>();

  // Update the availability list
  const setCurrentAvailability = useCallback((value: Record<string, unknown>) => {
    currentAvailabilitiesListRef.current = {
      ...currentAvailabilitiesListRef.current,
      [currentFlowIdRef.current]: value,
    };
  }, []);

  // Answers (used for replacements and patient creation)
  const answers = useRef<TLumaBotAnswers>({});

  // To show/hide the typing indicator during live chat
  const timerTypingRef = useRef(0);

  // mutations
  const flowCreateAndGetCurrentQuestion = useLumaBotFlowCreateOneWithCurrentQuestion();
  const flowCreateAnswer = useLumaBotFlowCreateAnswer();
  const flowRemoveAnswer = useLumaBotFlowDeleteAnswer();
  const patientLogin = usePatientLogin();
  const patientVerify = usePatientVerify();

  // const messageCreateOne = useMessageCreateOne();

  // Set initial locale from user's language
  useEffectOnce(() => {
    dispatch(changeLocale(normalizeLocale(language)));
  });

  // Handle messages for live chat
  useSocketEventsDebounced(['chat:server:outbound:message'], (array: any[]) => {
    if (!settings?.liveChat?.enabled) return;

    _.forEach(array, (data?: IMessage) => {
      const text = data?.text;
      const automated = data?.automated;
      const user = data?.user;
      if (text && !automated) {
        setState((prevState) => ({ ...prevState, isLiveChatEnabled: true, typing: false }));
        setTimeout(() => pushAnswer({ text, user, type: EMessageType.Inbound }, { skipNextItem: true }));
      }
    });
  });

  // Handle typing indicator for live chat
  useSocketEventsDebounced(['chat:server:typing:start'], (values) => {
    if (!settings?.liveChat?.enabled) return;

    setState((prevState) => ({ ...prevState, typing: true }));
    clearTimeout(timerTypingRef.current);

    // Hide indicator after $delay not typing
    timerTypingRef.current = window.setTimeout(() => {
      setState((prevState) => ({ ...prevState, typing: false }));
    }, 3000);
  });

  // useMemo so we can update texts on real-time when the language changes
  // TODO: Skip sanitizing the entire list of messages each time there's a change
  const messagesSanitized = useMemo(
    () =>
      sanitizeLumaBotMessages(
        state.messages,
        // messageTemplates,
        language,
        settings.name,
        answers.current,
        state.loading,
        state.typing,
      ),
    [state.messages, language, settings.name, state.loading, state.typing],
  );

  const _focusInput = () => inputRef.current?.focus();

  /**
   * Push flow template item (inbound message)
   */
  const pushItem: (item: ILumaBotFlowQuestion) => Promise<void> = useCallback(async (item) => {
    addQuery({ item: item.id });

    // Push new item's message
    setState((prevState) => ({
      ...prevState,
      loading: false,
      typing: false,
      currentItem: item,
      messages: _.concat(prevState.messages, {
        ...item,
        text: item.title,
        createdAt: moment().format(),
        type: EMessageType.Inbound,
        metadata: {
          item,
        },
      }),
    }));

    // Focus input
    _focusInput();
  }, []);

  const pushItems = useCallback(
    async (questions: ILumaBotFlowQuestion[]) => {
      const numberOfQuestions = questions.length;
      if (!numberOfQuestions) return;
      // eslint-disable-next-line no-restricted-syntax
      for (const question of questions) {
        setState((prevState) => ({ ...prevState, typing: true }));
        // eslint-disable-next-line no-await-in-loop
        await sleep(800 / numberOfQuestions);
        pushItem(question);
      }
    },
    [pushItem],
  );

  /**
   * Push flow template
   */
  const _pushFlowTemplate = useCallback(
    async (flowTemplate?: ILumaBotFlowTemplate, config?: { skipPushingItem?: boolean }) => {
      if (!flowTemplate) return;

      // // Disable live chat mode
      setState((prevState) => ({ ...prevState, isLiveChatEnabled: false }));

      // Update flowTemplate URL parameter for tracking purposes
      addQuery({ flowTemplate: flowTemplate._id });

      // Create flow and get initial questions
      const response = await flowCreateAndGetCurrentQuestion.mutateAsync(
        {
          body: {
            lumabotFlowTemplate: flowTemplate._id,
            previousLumabotFlow: _.last(messagesSanitized)?.lumabotFlowId,
          },
        },
        {
          onSuccess: (flow) => {
            currentFlowIdRef.current = flow._id;
            currentFlowTemplateIdRef.current = flow.lumabotFlowTemplate;
          },
        },
      );
      pushItems((response.currentQuestion as unknown) as ILumaBotFlowQuestion[]);
    },
    [flowCreateAndGetCurrentQuestion, messagesSanitized, pushItems],
  );

  /**
   * Move back to a specific message
   */
  const revertMessage: LumaBotContextProps['revertMessage'] = async (message) => {
    if (state.loading || state.typing) return;

    // Revert answer
    const question: any = message?.metadata?.item;
    await flowRemoveAnswer.mutateAsync(
      {
        queryParams: {
          flowId: question.lumabotFlowId,
          questionId: question.id,
        },
      },
      {
        onSuccess: () => {
          currentFlowIdRef.current = question.lumabotFlowId;
          currentFlowTemplateIdRef.current = question.lumabotFlowTemplate;
        },
      },
    );

    // Point to the reverted question
    setState((prevState) => {
      // @ts-ignore
      const index = _.findLastIndex(prevState.messages, { id: question?.id }) + 1;
      const messages = _.slice(prevState.messages, 0, index);
      return { ...prevState, messages };
    });

    // Focus input
    _focusInput();
  };

  /**
   * Wrapper to expose the `_pushFlowTemplate` private method
   */
  const pushFlowTemplate: LumaBotContextProps['pushFlowTemplate'] = useCallback(
    async (flowType, text) => {
      let flowTemplate = _.find(flowTemplates, { flowType });
      if (flowType === 'welcome' && flowId) {
        // Restart from the first flow the user accessed thru the url instead of the default Welcome
        flowTemplate = _.find(flowTemplates, { _id: flowId as string });
      }
      // Return in case there's no template or we're repeating the knock-knock
      if (!flowTemplate || flowType === ELumaBotFlowType.KnockKnock) {
        return;
      }
      if (text) {
        // render new message with the answer
        setState((prevState) => ({
          ...prevState,
          messages: _.concat(prevState.messages, {
            text,
            type: EMessageType.Outbound,
            createdAt: moment().format(),
            metadata: { includeRevert: false },
          }),
        }));
      }
      await _pushFlowTemplate(flowTemplate);
    },
    [flowTemplates, flowId, _pushFlowTemplate],
  );

  /**
   * Push message
   *
   * Used for patient messages (outbound), but it can also be used to push additional inbound messages
   * that are added after the initial flow template item
   *
   * E.g.: validation messages (wrong email, ...), or 2FA messages (code sent, ...)
   */
  const pushAnswer: LumaBotContextProps['pushAnswer'] = useCallback(
    async (message, opts = { customDelay: 0, skipNextItem: false }) => {
      const isInbound = message.type !== EMessageType.Outbound;
      const metadata = message.metadata;

      if (isInbound) {
        // Delaying 1sec for better UX when there's gonna be an inbound message from the bot
        // E.g. for validation error messages
        setState((prevState) => ({ ...prevState, loading: true }));
        await sleep(800);
      }
      // render new message with the answer
      setState((prevState) => ({
        ...prevState,
        loading: true,
        messages: _.concat(prevState.messages, {
          ...message,
          createdAt: moment().format(),
        }),
      }));

      // Skip fetching more questions
      if (opts.skipNextItem || state.isLiveChatEnabled) {
        setState((prevState) => ({ ...prevState, loading: false }));
        _focusInput();
        return;
      }

      // Submit outbound message on live chat mode
      // if (state.isLiveChatEnabled && newMessage.type === 'outbound') {
      //   await messageCreateOne.mutateAsync(_.pick(message, ['text']));
      // }

      // Update answers
      const squiggly = message?.metadata?.item?.squiggly;
      if (squiggly) {
        const value = message?.metadata?.answer?.label || message?.metadata?.answer || message.text;
        answers.current[squiggly] = value;
      }
      // Message requires sending the 2FA code to the patient
      if (message?.metadata?.item?.type === ELumaBotQuestionType.TwoFA) {
        const patientData = { ...getLumaBotPatientData(answers.current), language };
        try {
          setState((prevState) => ({ ...prevState, shouldDisableHeaderMenuActions: true }));
          const patient = await patientLogin.mutateAsync(
            {
              patient: patientData,
              shouldCreatePatientOnLoginFailure: true,
              createPatientData: {
                ...patientData,
                language,
                // @ts-ignore
                source: 'widget',
              },
            },
            {
              onSettled: () => setState((prevState) => ({ ...prevState, shouldDisableHeaderMenuActions: false })),
            },
          );
          await pushAnswer(
            {
              text: generateMessageTemplateKey('patientLumaBot2FASendCodeSuccess'),
              metadata: {
                answer: _.pick(message?.metadata?.answer, ['id']),
                item: _.pick(message?.metadata?.item, ['lumabotFlowId']) as any,
                patient,
                forceInput: true,
                includeRevert: false,
              },
              type: EMessageType.Inbound,
            },
            { skipNextItem: true },
          );
          _focusInput();
          return;
        } catch (error) {
          pushAnswer(
            {
              text: generateMessageTemplateKey('patientLumaBot2FASendCodeError'),
              type: EMessageType.Inbound,
            },
            { skipNextItem: true },
          );
          pushFlowTemplate(ELumaBotFlowType.Error);
          return;
        }
      }
      // Verify patient with the 2FA code
      if (message.type === EMessageType.Outbound && message.metadata?.patient) {
        try {
          await patientVerify.mutateAsync(
            {
              patientId: message.metadata.patient!,
              code: message.text!,
            },
            {
              onSuccess: async (response) => {
                storageService.setToken(response.token);
                await new Promise((resolve, reject) => {
                  dispatch(
                    initApp.request({
                      location: ({ pathname: '/lumabot' } as unknown) as Location,
                      meta: {
                        callbackSuccess: resolve,
                        callbackError: reject,
                      },
                    }),
                  );
                });
              },
              onError: (error) => {
                dispatch(showMessage(error.message, 'error'));
              },
            },
          );
          pushAnswer(
            {
              text: generateMessageTemplateKey('patientLumaBot2FAVerifyCodeSuccess'),
              metadata: {
                item: _.pick(message?.metadata?.item, ['lumabotFlowId']) as any,
                patient: message.metadata.patient,
              },
              type: EMessageType.Inbound,
            },
            { skipNextItem: true },
          );
        } catch (error) {
          if (_.get(error, 'response.status') === STATUS_CODE_FORBIDDEN) {
            pushAnswer(
              {
                text: generateMessageTemplateKey('patientLumaBot2FAVerifyCodeError'),
                metadata: { ...message.metadata },
                type: EMessageType.Inbound,
              },
              { skipNextItem: true },
            );
            return;
          }
          pushFlowTemplate(ELumaBotFlowType.Error);
          return;
        }
      }

      // Submit the answer
      let answerId = message?.metadata?.answer?.id;
      if (message?.metadata?.prevMessage?.metadata?.item?.type === ELumaBotQuestionType.TwoFA) {
        answerId = message?.metadata?.prevMessage?.metadata?.answer?.id;
      }

      const nextQuestions = await flowCreateAnswer.mutateAsync(
        {
          queryParams: {
            flowId: metadata?.item?.lumabotFlowId || currentFlowIdRef.current,
          },
          body: {
            id: answerId,
            text: metadata?.answer?.label || message?.text,
            details: message.details,
          },
        },
        {
          onError: () => pushFlowTemplate(ELumaBotFlowType.Error),
          onSuccess: (questions) => {
            currentFlowIdRef.current = _.last(questions)?.lumabotFlowId!;
            currentFlowTemplateIdRef.current = _.last(questions)?.lumabotFlowTemplate!;
          },
        },
      );

      // Handle redirects
      const redirectType = metadata?.answer?.redirectType || metadata?.item?.redirectType;
      if (redirectType === ELumaBotTemplateItemRedirectType.Scheduler) {
        const { facility, provider, appointmentType, ...params } =
          // @ts-ignore
          metadata?.answer?.params || metadata?.item?.params || metadata?.item?.properties.params;

        storageService.extendParameters({
          ...params,
          facility: facility?.join(','),
          provider: provider?.join(','),
          appointmentType: appointmentType?.join(','),
          patientFormTemplate: metadata?.flowTemplateId,
        });
        // Availabilities / Book screens uses the url parameters to control the filters.
        // Since we need a safe place to get them from, I'm saving the params on the localStorage
        storageService.set(STORAGE_KEYS.patientFormPSFilters, {
          ...params,
          facility: facility?.join(','),
          provider: provider?.join(','),
          appointmentType: appointmentType?.join(','),
          patientFormTemplate: metadata?.flowTemplateId,
        });
        const { user, ...storageParams } = storageService.getParameters();
        const webSchedulerUrl = `${configService.getSitePatient()}/availabilities?${queryString.stringify({
          ...storageParams,
          token: storageService.getToken(),
        })}`;
        const searchParams = new URLSearchParams(window.location.search);
        const { widget } = Object.fromEntries(searchParams.entries());
        if (window.parent && widget === 'popper') {
          const action = 'openUrl';
          window.parent.postMessage({ caller: 'lh:lumabot', action, value: webSchedulerUrl }, '*');
        } else {
          window.open(webSchedulerUrl, '_blank');
        }
        pushFlowTemplate(ELumaBotFlowType.CulDeSac);
        return;
      }

      if (redirectType === ELumaBotTemplateItemRedirectType.ExternalUrl) {
        const searchParams = new URLSearchParams(window.location.search);
        const { widget } = Object.fromEntries(searchParams.entries());
        const url = metadata?.answer?.redirectValue;
        if (window.parent && widget === 'popper') {
          const action = 'openUrl';
          window.parent.postMessage({ caller: 'lh:lumabot', action, value: url }, '*');
        } else {
          window.open(url, '_blank');
        }
        pushFlowTemplate(ELumaBotFlowType.CulDeSac);
        return;
      }

      // Push CulDeSac if BE returns empty response (indicating the flow has ended)
      if (!nextQuestions || !nextQuestions.length) {
        pushFlowTemplate(ELumaBotFlowType.CulDeSac);
        return;
      }

      // render next items
      pushItems(nextQuestions as any);

      if (redirectType === ELumaBotTemplateItemRedirectType.IsFinal) {
        pushFlowTemplate(ELumaBotFlowType.CulDeSac);
        return;
      }

      // Push CulDeSac if users can't answer next last question and its set as final
      const lastNextQuestion = nextQuestions[nextQuestions.length - 1];
      if (
        lastNextQuestion?.redirectType === ELumaBotTemplateItemRedirectType.IsFinal &&
        (lastNextQuestion?.type === ELumaBotQuestionType.Template ||
          lastNextQuestion?.type === ELumaBotQuestionType.AvailabilityDetails)
      ) {
        pushFlowTemplate(ELumaBotFlowType.CulDeSac);
      }
    },

    [
      state.isLiveChatEnabled,
      flowCreateAnswer,
      pushItems,
      language,
      patientLogin,
      patientVerify,
      dispatch,
      pushFlowTemplate,
    ],
  );

  /**
   * Clear the state. E.g.: when the session expires
   */
  const clear = () => {
    currentItemRef.current = undefined;
    setState(chatContextDefaultValue);
    currentFlowIdRef.current = '';
    currentFlowTemplateIdRef.current = '';
    currentAvailabilitiesListRef.current = {};
  };

  // Initialize the chat
  useEffect(() => {
    if (flowTemplates) {
      const flow = _.find(flowTemplates, flowId ? { _id: flowId } : { flowType: ELumaBotFlowType.Welcome });
      _pushFlowTemplate(flow as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowTemplates]);

  return {
    ...state,
    inputRef,
    settings,
    flowTemplates,
    flowId,
    variant,
    messages: messagesSanitized,
    currentFlowId: currentFlowIdRef.current,
    currentFlowTemplateId: currentFlowTemplateIdRef.current,
    setCurrentAvailability,
    currentAvailabilitiesList: currentAvailabilitiesListRef.current,
    currentItem: currentItemRef.current,
    isPopper,
    setMode: (newMode) => setState((prevState) => ({ ...prevState, mode: newMode })),
    pushFlowTemplate,
    pushAnswer,
    revertMessage,
    clear,
  };
}
